import React, { useCallback, useEffect, useState } from 'react';
import { useLeague, useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import type { League, LeagueFormat, FixtureStatus } from '@shootout-comps/types';
import DivisionTabs from '../../components/DivisionTabs';
import FixtureCalendar from '../../components/FixtureCalendar';
import DateTimeField from '../../components/DateTimeField';

interface DivisionFixturesPageProps {
  leagueId: string;
  divisionId: string;
  onBack: () => void;
  onSwitchToTeams: () => void;
  onSwitchToStandings: () => void;
  onSwitchToHistory: () => void;
  onOpenFixture: (fixtureId: string) => void;
  onOpenDateFixtures: (dateParam: string) => void;
}

const FORMAT_LABEL: Record<LeagueFormat, string> = { handicap: '🔴 Handicap', scratch: '🎯 Scratch' };

export default function DivisionFixturesPage({ leagueId, divisionId, onBack, onSwitchToTeams, onSwitchToStandings, onSwitchToHistory, onOpenFixture, onOpenDateFixtures }: DivisionFixturesPageProps) {
  const { fetchLeague, generateFixtures, addFixture } = useLeague();
  const { user } = useAuth();
  const { t } = useLang();
  const STATUS_LABEL: Record<FixtureStatus, { label: string; color: string }> = {
    scheduled: { label: t.fixtureStatusScheduled, color: 'var(--chalk-dim)' },
    lineup_set: { label: t.fixtureStatusLineupSet, color: '#3498db' },
    in_progress: { label: t.fixtureStatusInProgress, color: '#2ecc71' },
    complete: { label: t.complete, color: 'var(--gold)' },
  };
  const [league, setLeague] = useState<League | null>(null);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [doubleRoundRobin, setDoubleRoundRobin] = useState(false);

  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [showAddFixture, setShowAddFixture] = useState(false);
  const [addDate, setAddDate] = useState<Date | null>(null);
  const [addVenue, setAddVenue] = useState('');
  const [addRows, setAddRows] = useState<{ homeTeamId: string; awayTeamId: string; error?: string }[]>([{ homeTeamId: '', awayTeamId: '' }]);
  const [addingFixture, setAddingFixture] = useState(false);

  const reload = useCallback(() => {
    fetchLeague(leagueId).then(setLeague).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [fetchLeague, leagueId]);

  useEffect(reload, [reload]);

  const division = league?.divisions.find(d => d._id === divisionId);

  const handleGenerate = async () => {
    setGenerating(true); setError('');
    try {
      const updated = await generateFixtures(leagueId, divisionId, doubleRoundRobin);
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate fixtures');
    } finally {
      setGenerating(false);
    }
  };

  const resetAddPanel = () => {
    setShowAddFixture(false); setAddDate(null); setAddVenue(''); setAddRows([{ homeTeamId: '', awayTeamId: '' }]);
  };

  const updateRow = (idx: number, field: 'homeTeamId' | 'awayTeamId', value: string) => {
    setAddRows(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value, error: undefined } : r)));
  };

  const addRow = () => setAddRows(prev => [...prev, { homeTeamId: '', awayTeamId: '' }]);
  const removeRow = (idx: number) => setAddRows(prev => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const validRowCount = addRows.filter(r => r.homeTeamId && r.awayTeamId && r.homeTeamId !== r.awayTeamId).length;

  // Submits every valid pairing sequentially (not in parallel — each call reads-then-saves the
  // whole league doc server-side, so concurrent calls would race and lose updates), all sharing
  // the one date/venue entered above. Rows that fail (or were left half-filled) stay in the panel
  // with their error so the user can fix just those, instead of losing the whole batch.
  const handleAddFixture = async () => {
    setAddingFixture(true); setError('');
    let updated: League | null = null;
    const remaining: typeof addRows = [];
    for (const row of addRows) {
      if (!row.homeTeamId || !row.awayTeamId || row.homeTeamId === row.awayTeamId) {
        if (row.homeTeamId || row.awayTeamId) remaining.push({ ...row, error: 'Pick both a home and away team' });
        continue;
      }
      try {
        updated = await addFixture(leagueId, divisionId, {
          homeTeamId: row.homeTeamId, awayTeamId: row.awayTeamId,
          scheduledAt: addDate ? addDate.toISOString() : null,
          venue: addVenue.trim() || null,
        });
      } catch (e) {
        remaining.push({ ...row, error: e instanceof Error ? e.message : 'Failed to add' });
      }
    }
    if (updated) setLeague(updated);
    setAddingFixture(false);
    if (remaining.length === 0) resetAddPanel();
    else setAddRows(remaining);
  };

  if (error && !league) return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{error}</div>;
  if (!league || !division) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>Loading…</div>;

  const canGenerate = division.teams.length >= 2 && !division.seasonStartedAt;

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
        ← {league.name}
      </button>

      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.5rem,5vw,2rem)', color: 'var(--gold)', marginBottom: '0.5rem' }}>
        {division.name} <span style={{ fontSize: '0.9rem', color: 'var(--chalk-dim)', fontFamily: 'inherit' }}>{FORMAT_LABEL[division.format]}</span>
      </h2>

      <DivisionTabs active="fixtures" onSelect={tab => { if (tab === 'teams') onSwitchToTeams(); else if (tab === 'standings') onSwitchToStandings(); else if (tab === 'history') onSwitchToHistory(); }} />

      {error && <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginBottom: '1rem' }}>{error}</p>}

      {division.teams.length >= 2 && (
        <div style={{ marginBottom: '1.25rem' }}>
          {showAddFixture ? (
            <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--chalk-dim)' }}>
                Add several fixtures at once — the date and venue below apply to every pairing.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <DateTimeField
                  value={addDate}
                  onChange={setAddDate}
                  dateFormat={user?.dateFormat ?? 'DMY'}
                />
                <input
                  value={addVenue}
                  onChange={e => setAddVenue(e.target.value)}
                  placeholder="Venue (optional)"
                  style={{ flex: 1, minWidth: '140px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.5rem 0.6rem', color: 'var(--chalk)', fontSize: '0.82rem', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {addRows.map((row, idx) => (
                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <select
                        value={row.homeTeamId}
                        onChange={e => updateRow(idx, 'homeTeamId', e.target.value)}
                        style={{ flex: 1, minWidth: '120px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${row.error ? 'rgba(231,76,60,0.5)' : 'rgba(255,255,255,0.15)'}`, borderRadius: '6px', padding: '0.5rem 0.6rem', color: 'var(--chalk)', fontSize: '0.82rem', outline: 'none' }}
                      >
                        <option value="">Home team</option>
                        {division.teams.map(tm => <option key={tm._id} value={tm._id} disabled={tm._id === row.awayTeamId}>{tm.name}</option>)}
                      </select>
                      <span style={{ color: 'var(--chalk-dim)', fontSize: '0.75rem' }}>vs</span>
                      <select
                        value={row.awayTeamId}
                        onChange={e => updateRow(idx, 'awayTeamId', e.target.value)}
                        style={{ flex: 1, minWidth: '120px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${row.error ? 'rgba(231,76,60,0.5)' : 'rgba(255,255,255,0.15)'}`, borderRadius: '6px', padding: '0.5rem 0.6rem', color: 'var(--chalk)', fontSize: '0.82rem', outline: 'none' }}
                      >
                        <option value="">Away team</option>
                        {division.teams.map(tm => <option key={tm._id} value={tm._id} disabled={tm._id === row.homeTeamId}>{tm.name}</option>)}
                      </select>
                      <button
                        onClick={() => removeRow(idx)}
                        disabled={addRows.length === 1}
                        title="Remove this pairing"
                        style={{ background: 'transparent', border: 'none', color: 'var(--chalk-dim)', fontSize: '1rem', cursor: addRows.length === 1 ? 'default' : 'pointer', opacity: addRows.length === 1 ? 0.3 : 1, flexShrink: 0, padding: '0.2rem 0.4rem' }}
                      >
                        ✕
                      </button>
                    </div>
                    {row.error && <p style={{ margin: 0, fontSize: '0.7rem', color: '#e74c3c' }}>{row.error}</p>}
                  </div>
                ))}
              </div>

              <button
                onClick={addRow}
                style={{ alignSelf: 'flex-start', background: 'transparent', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.7rem', fontSize: '0.75rem', cursor: 'pointer' }}
              >
                + Add another pairing
              </button>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleAddFixture}
                  disabled={validRowCount === 0 || addingFixture}
                  style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.55rem 1.1rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', opacity: validRowCount === 0 ? 0.5 : 1 }}
                >
                  {addingFixture ? '…' : validRowCount > 1 ? `+ Add ${validRowCount} Fixtures` : '+ Add Fixture'}
                </button>
                <button
                  onClick={resetAddPanel}
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.55rem 1.1rem', color: 'var(--chalk-dim)', fontSize: '0.82rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddFixture(true)}
              style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--gold)', border: '1px dashed rgba(201,168,76,0.35)', borderRadius: '10px', padding: '0.6rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', width: '100%' }}
            >
              + Add Fixture
            </button>
          )}
        </div>
      )}

      {!division.seasonStartedAt ? (
        <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px dashed rgba(201,168,76,0.35)', borderRadius: '10px', padding: '1.25rem', textAlign: 'center', marginBottom: '1.25rem' }}>
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            {division.teams.length < 2
              ? 'Add at least 2 teams before generating fixtures.'
              : `Generate a full round-robin — every one of the ${division.teams.length} teams plays every other team once.`}
          </p>
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem', cursor: canGenerate ? 'pointer' : 'default', marginBottom: '1rem', fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>
            <input
              type="checkbox"
              checked={doubleRoundRobin}
              disabled={!canGenerate}
              onChange={e => setDoubleRoundRobin(e.target.checked)}
              style={{ accentColor: 'var(--gold)', width: '15px', height: '15px' }}
            />
            {t.doubleRoundRobinLabel}
          </label>
          <button
            onClick={handleGenerate}
            disabled={!canGenerate || generating}
            style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.65rem 1.25rem', fontSize: '0.85rem', fontWeight: 700, cursor: canGenerate ? 'pointer' : 'not-allowed', opacity: canGenerate ? 1 : 0.5 }}
          >
            {generating ? '…' : '📅 Generate Fixtures'}
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
            {(['list', 'calendar'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                style={{
                  background: viewMode === mode ? 'var(--gold)' : 'transparent',
                  color: viewMode === mode ? 'var(--baize-dark)' : 'var(--chalk-dim)',
                  border: `1px solid ${viewMode === mode ? 'var(--gold)' : 'rgba(255,255,255,0.15)'}`,
                  borderRadius: '6px', padding: '0.35rem 0.85rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
                }}
              >
                {mode === 'list' ? '☰ List' : '🗓 Calendar'}
              </button>
            ))}
          </div>

          {viewMode === 'calendar' ? (
            <FixtureCalendar
              fixtures={division.fixtures}
              onSelectDate={onOpenDateFixtures}
              onOpenFixture={onOpenFixture}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {division.fixtures.map(f => {
                const status = STATUS_LABEL[f.status];
                return (
                  <div
                    key={f._id}
                    onClick={() => onOpenFixture(f._id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.9rem 1.1rem', cursor: 'pointer' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        {f.homeTeamName} <span style={{ color: 'var(--chalk-dim)', fontWeight: 400 }}>vs</span> {f.awayTeamName}
                        {f.status === 'complete' && (
                          <span style={{ color: 'var(--gold)', fontWeight: 700, marginLeft: '0.5rem' }}>{f.homeFrameWins}–{f.awayFrameWins}</span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.73rem', color: 'var(--chalk-dim)', marginTop: '0.2rem' }}>
                        {f.scheduledAt ? new Date(f.scheduledAt).toLocaleString() : 'Not scheduled'}{f.venue ? ` · ${f.venue}` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: status.color, whiteSpace: 'nowrap' }}>{status.label}</span>
                    <span style={{ color: 'var(--chalk-dim)' }}>›</span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
